// Browser geolocation for "near me" questions. The permission prompt
// only ever fires at the moment the visitor asks a near-me question,
// never on widget load.

// Keep in sync with the server-side copy in api/src/us-geo.ts. A named
// place ("near Cranberry") deliberately does NOT match; those resolve
// server-side against localities/states.
const NEAR_ME_RE =
  /\b(near\s+me|near\s+us|nearby|close\s+(?:to|by)\s*(?:me|us)\b|close\s+by|closest\s+to\s+(?:me|us)|in\s+my\s+area|around\s+(?:me|here)|in\s+the\s+area)\b/i;

export interface UserLocation {
  lat: number;
  lng: number;
}

export function isNearMeQuery(text: string): boolean {
  return NEAR_ME_RE.test(text);
}

// Resolve the visitor's coordinates, or null on denial, timeout, missing
// API, or insecure context. Never throws and never surfaces an error:
// the chat continues without coordinates and the server answers
// generally. Coordinates are rounded to 3 decimals (~110 m), which is
// ample for mile-scale distance ranking and avoids sending a precise
// position. maximumAge lets the browser reuse a recent fix.
export function getUserLocation(
  timeoutMs = 8000,
): Promise<UserLocation | null> {
  return new Promise((resolve) => {
    try {
      if (
        typeof navigator === "undefined" ||
        !("geolocation" in navigator)
      ) {
        resolve(null);
        return;
      }
      const round = (v: number) => Math.round(v * 1000) / 1000;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            lat: round(pos.coords.latitude),
            lng: round(pos.coords.longitude),
          });
        },
        () => resolve(null),
        {
          enableHighAccuracy: false,
          timeout: timeoutMs,
          maximumAge: 300000,
        },
      );
    } catch {
      resolve(null);
    }
  });
}
